/**
 * What sport a page is about, canonically.
 *
 * **No Zod in this file**, for the reason `money.ts` gives at length: it is
 * reached from the browser as `@geni/contracts/sport`, and the package root
 * would put a validation library on a betting page to answer "is this tennis".
 *
 * Why a canonical set at all, when `Event.sport` is deliberately the feed's own
 * free-text word: because this one is used to *look up a translated string*.
 * A key built from whatever a feed happened to write — "Soccer", "soccer_epl",
 * "Fútbol" — is a key that exists in no string table, so the widget would show
 * nothing in exactly the markets where a feed is least likely to speak English.
 * The feed's wording stays untouched wherever it is *displayed*; this is only
 * for deciding which of our own sentences to reach for.
 */

/**
 * The sports the string tables have prompts for, plus a catch-all.
 *
 * `OTHER` is not a failure — it is an operator trading something we have no
 * suggestions written for, which is most of a real sportsbook. It renders no
 * starter prompts rather than a wrong one, and everything else about the
 * assistant works exactly as it does anywhere else.
 */
export const SPORT_KINDS = [
  'SOCCER',
  'AMERICAN_FOOTBALL',
  'BASKETBALL',
  'TENNIS',
  'GOLF',
  'ESPORTS',
  'OTHER',
] as const;
export type SportKind = (typeof SPORT_KINDS)[number];

/**
 * Words a feed or an operator's page actually uses, mapped to ours.
 *
 * Matched on a normalised substring rather than exactly, because the same sport
 * arrives as "Soccer", "soccer_epl", "Football" and "Fútbol" depending on who
 * is writing, and an operator who has to look up our spelling to make the
 * feature work will simply not use the feature.
 *
 * Order matters where two entries could both hit. "Counter-Strike" contains no
 * other sport's name, but "American Football" contains "football" — so the
 * esports and American-football checks run before the soccer one, and a test
 * pins that.
 */
const ALIASES: readonly (readonly [SportKind, readonly string[]])[] = [
  /**
   * Before SOCCER, because every one of these words contains "football".
   *
   * `football_nfl` is here as a whole key rather than left to the `nfl` word,
   * because a feed key is exactly what an operator pastes when they are wiring
   * this up for the first time and have not read far enough to find the
   * canonical spelling.
   */
  ['AMERICAN_FOOTBALL', ['american football', 'americanfootball', 'football_nfl', 'nfl', 'cfl', 'gridiron']],
  // Also before SOCCER, and for the same reason: "handball" ends in one.
  ['OTHER', ['handball', 'futsal']],
  ['ESPORTS', ['esport', 'e-sport', 'counter-strike', 'counterstrike', 'cs:go', 'csgo', 'cs2', 'dota', 'league of legends', 'valorant', 'overwatch', 'rocket league']],
  ['SOCCER', ['soccer', 'football', 'futbol', 'fútbol', 'futebol', 'fussball', 'fußball', 'calcio', 'futbol', 'футбол']],
  ['BASKETBALL', ['basketball', 'basket', 'nba', 'euroleague', 'baloncesto', 'basquete', 'баскетбол']],
  ['TENNIS', ['tennis', 'tenis', 'tênis', 'atp', 'wta', 'теннис']],
  ['GOLF', ['golf', 'pga', 'gольф', 'гольф']],
];

/**
 * Read a sport out of whatever the operator wrote.
 *
 * Returns null for an empty or absent value — which is different from `OTHER`.
 * Null means nobody said, and the widget behaves exactly as it did before sport
 * context existed; `OTHER` means somebody said, and it was a sport we have
 * nothing written for. Collapsing the two would make a missing attribute
 * indistinguishable from a misconfigured one.
 */
export function normaliseSport(raw: string | null | undefined): SportKind | null {
  if (raw === null || raw === undefined) return null;

  const value = raw.trim().toLowerCase();
  if (value === '') return null;

  // An exact canonical value wins outright, so an operator who reads the docs
  // and sends "ESPORTS" is never at the mercy of the alias list.
  const exact = SPORT_KINDS.find((kind) => kind.toLowerCase() === value);
  if (exact !== undefined) return exact;

  for (const [kind, words] of ALIASES) {
    if (words.some((word) => value.includes(word))) return kind;
  }

  return 'OTHER';
}
